/**
 * BusinessRendimientos — la vitrina: dónde PUEDE trabajar la plata que espera.
 *
 * Mismo patrón visual que el catálogo de fondos del dashboard de personas:
 * tarjetas de catálogo, nunca asignaciones. La regla de oro va arriba y sin
 * letra chica: FlowexPay no mueve la plata de nadie — el dueño decide y
 * confirma con su código. CUÁNTO guardar y para cuándo vive en Provisiones.
 *
 * Visual: .rendimientos-ola + tokens --fx-* (glass Ola A). Solo chrome.
 * La vitrina sale de la política de tesorería (data/politicaTesoreria.js via
 * /api/business-sobres/escalera): una sola fuente para pantalla y Oráculo.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  escaleraGet, rendimientosPosicion, rendimientosPreparar,
  rendimientosSendCode, rendimientosActivar, rendimientosRetirar,
} from '../../lib/cerebro';

const fmtCop = (n) => `$${Math.round(Number(n || 0)).toLocaleString('es-CO')}`;
const fmtUsd = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;

/**
 * La tarjeta de Rewards FUNCIONAL: el primer rendimiento que la empresa puede
 * activar de verdad. Flujo completo dentro de la tarjeta: monto -> preview con
 * tasa pactada -> codigo SMS (al numero registrado, lo decide el servidor) ->
 * supply on-chain. El retiro usa el mismo riel de codigo.
 */
function TarjetaRewards({ inst, pos, onRefresh }) {
  const [paso, setPaso] = useState('reposo'); // reposo | monto | preview | codigo | ejecutando | exito
  const [monto, setMonto] = useState('');
  const [preview, setPreview] = useState(null);
  const [codigo, setCodigo] = useState('');
  const [smsInfo, setSmsInfo] = useState('');
  const [msg, setMsg] = useState('');
  const [exito, setExito] = useState(null);
  const [retirando, setRetirando] = useState(null); // id de la posicion en retiro (pide codigo)
  const [busy, setBusy] = useState(false);

  const preparar = async () => {
    const m = Number(String(monto).replace(/[^0-9.]/g, ''));
    if (!m || busy) return;
    setBusy(true); setMsg('');
    try {
      const r = await rendimientosPreparar(m);
      if (r?.ok) { setPreview(r); setPaso('preview'); }
      else setMsg(r?.mensaje || 'No pude preparar la activación.');
    } catch { setMsg('No pude preparar la activación. Intenta de nuevo.'); }
    setBusy(false);
  };

  const pedirCodigo = async () => {
    if (busy) return;
    setBusy(true); setMsg('');
    try {
      const r = await rendimientosSendCode();
      if (r?.ok) { setSmsInfo(`Código enviado al ${r.enviadoA}`); setPaso('codigo'); }
      else setMsg(r?.mensaje || 'No pude enviarte el código.');
    } catch { setMsg('No pude enviarte el código. Intenta de nuevo.'); }
    setBusy(false);
  };

  const activar = async () => {
    if (!codigo || busy) return;
    setBusy(true); setMsg(''); setPaso('ejecutando');
    try {
      const r = await rendimientosActivar(preview.token, codigo);
      if (r?.ok) { setExito(r); setPaso('exito'); onRefresh(); }
      else { setMsg(r?.mensaje || 'La activación no se confirmó.'); setPaso('codigo'); }
    } catch { setMsg('La activación falló. Revisa antes de reintentar.'); setPaso('codigo'); }
    setBusy(false);
  };

  const retirar = async (id) => {
    if (!codigo || busy) return;
    setBusy(true); setMsg('');
    try {
      const r = await rendimientosRetirar(id, codigo);
      if (r?.ok) {
        setMsg(`Retiro confirmado${r.rendimientoNetoUsd != null ? ` — rendimiento neto ${fmtUsd(r.rendimientoNetoUsd)}` : ''}. La plata vuelve a tu cuenta.`);
        setRetirando(null); setCodigo(''); onRefresh();
      } else setMsg(r?.mensaje || 'El retiro no se confirmó.');
    } catch { setMsg('El retiro falló. La posición sigue activa.'); }
    setBusy(false);
  };

  const reiniciar = () => { setPaso('reposo'); setMonto(''); setPreview(null); setCodigo(''); setSmsInfo(''); setMsg(''); setExito(null); };
  const posiciones = pos?.posiciones || [];

  return (
    <div className="rendimientos-ola__card rendimientos-ola__card--live">
      <div className="rendimientos-ola__card-top">
        <span className="rendimientos-ola__card-name">{inst.nombre}</span>
        <span className="rendimientos-ola__card-rate">
          {pos?.tasaVigentePct != null ? `${pos.tasaVigentePct}% anual neta hoy` : inst.tasaRef}
        </span>
      </div>
      <div className="rendimientos-ola__card-meta">Para plata sin fecha · la tasa queda pactada al activar</div>
      <p className="rendimientos-ola__card-why">{inst.porQue}</p>

      {/* Posiciones activas: la plata que YA está trabajando. */}
      {posiciones.map((p) => (
        <div key={p.id} className="rendimientos-ola__pos">
          <div className="rendimientos-ola__pos-row">
            <div>
              <div className="rendimientos-ola__pos-amt">{fmtUsd(p.montoUsd)} trabajando</div>
              <div className="rendimientos-ola__pos-meta">
                {p.tasaPactadaPct != null ? `${p.tasaPactadaPct}% pactado` : ''}{p.estado !== 'active' ? ` · ${p.estado}` : ''}
              </div>
            </div>
            {p.estado === 'active' && retirando !== p.id && (
              <button
                type="button"
                onClick={async () => { setRetirando(p.id); setCodigo(''); setMsg(''); const r = await rendimientosSendCode(); if (r?.ok) setSmsInfo(`Código enviado al ${r.enviadoA}`); }}
                className="rendimientos-ola__text-btn"
              >
                Retirar
              </button>
            )}
          </div>
          {retirando === p.id && (
            <div className="rendimientos-ola__flow-row">
              <input
                value={codigo} onChange={(e) => setCodigo(e.target.value)} placeholder="Código SMS"
                inputMode="numeric" maxLength={6}
                className="rendimientos-ola__input"
                aria-label="Código SMS"
              />
              <button type="button" onClick={() => retirar(p.id)} disabled={busy} className="rendimientos-ola__btn rendimientos-ola__btn--primary">
                {busy ? '…' : 'Confirmar retiro'}
              </button>
            </div>
          )}
        </div>
      ))}

      {/* El flujo de activación, paso a paso dentro de la tarjeta. */}
      {paso === 'reposo' && (
        <button
          type="button"
          onClick={() => { setPaso('monto'); setMsg(''); }}
          className="rendimientos-ola__btn rendimientos-ola__btn--primary rendimientos-ola__btn--block"
        >
          Activar — tú confirmas con tu código
        </button>
      )}
      {paso === 'monto' && (
        <div className="rendimientos-ola__flow">
          <div className="rendimientos-ola__hint">Disponible: {fmtUsd(pos?.saldoUsd)}</div>
          <div className="rendimientos-ola__flow-row">
            <input
              value={monto} onChange={(e) => setMonto(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && preparar()}
              placeholder="Monto en dólares" inputMode="decimal"
              className="rendimientos-ola__input"
              aria-label="Monto en dólares"
            />
            <button type="button" onClick={preparar} disabled={busy} className="rendimientos-ola__btn rendimientos-ola__btn--primary">
              {busy ? '…' : 'Ver preview'}
            </button>
          </div>
          <button type="button" onClick={reiniciar} className="rendimientos-ola__cancel">Cancelar</button>
        </div>
      )}
      {paso === 'preview' && preview && (
        <div className="rendimientos-ola__preview">
          <div className="rendimientos-ola__preview-title">{fmtUsd(preview.montoUsd)} → Rewards al {preview.tasaPactadaPct}% pactado</div>
          <div className="rendimientos-ola__hint">Saldo después: {fmtUsd(preview.saldoDespuesUsd)} · el preview vale {preview.expiraEnMin} min</div>
          <div className="rendimientos-ola__hint">{preview.nota}</div>
          <div className="rendimientos-ola__flow-row pt">
            <button type="button" onClick={pedirCodigo} disabled={busy} className="rendimientos-ola__btn rendimientos-ola__btn--good">
              {busy ? '…' : 'Confirmar con mi código'}
            </button>
            <button type="button" onClick={reiniciar} className="rendimientos-ola__cancel">Cancelar</button>
          </div>
        </div>
      )}
      {paso === 'codigo' && (
        <div className="rendimientos-ola__flow">
          {smsInfo && <div className="rendimientos-ola__sms">{smsInfo}</div>}
          <div className="rendimientos-ola__flow-row">
            <input
              value={codigo} onChange={(e) => setCodigo(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && activar()}
              placeholder="Código de 6 dígitos" inputMode="numeric" maxLength={6}
              className="rendimientos-ola__input"
              aria-label="Código de 6 dígitos"
            />
            <button type="button" onClick={activar} disabled={busy || !codigo} className="rendimientos-ola__btn rendimientos-ola__btn--good">
              Activar
            </button>
          </div>
          <button type="button" onClick={reiniciar} className="rendimientos-ola__cancel">Cancelar</button>
        </div>
      )}
      {paso === 'ejecutando' && (
        <div className="rendimientos-ola__hint mt">Activando en la blockchain… esto toma unos segundos, no cierres la página.</div>
      )}
      {paso === 'exito' && exito && (
        <div className="rendimientos-ola__pos">
          <div className="rendimientos-ola__pos-amt">Listo: {fmtUsd(exito.montoUsd)} trabajando al {exito.tasaNetaPct}%</div>
          {exito.explorerUrl && (
            <a href={exito.explorerUrl} target="_blank" rel="noreferrer" className="rendimientos-ola__link">Ver la transacción en la blockchain</a>
          )}
          <button type="button" onClick={reiniciar} className="rendimientos-ola__cancel block">Cerrar</button>
        </div>
      )}

      {msg && <div className="rendimientos-ola__alert">{msg}</div>}
    </div>
  );
}

const ESTADOS = {
  // Honestidad ante todo: el motor de activación empresarial aún no existe —
  // decir "tú lo activas con tu código" prometía un botón que no está.
  ejecutable: { label: 'Permitido por la política. El botón para activarlo — con tu código, como todo — está en construcción.', tone: 'good' },
  en_vitrina: { label: 'Próximamente: estamos terminando la conexión con Progresión para que lo actives desde aquí.', tone: 'warn' },
  en_tramite: { label: 'En trámite: se activa cuando se firme el contrato. No requiere que hagas nada.', tone: 'muted' },
};

/** Tarjeta de catálogo de un instrumento: qué es, cuánto referencia y su estado. */
function TarjetaVitrina({ inst }) {
  const estado = ESTADOS[inst.estado] || ESTADOS.en_vitrina;
  return (
    <div className="rendimientos-ola__card">
      <div className="rendimientos-ola__card-top">
        <span className="rendimientos-ola__card-name">{inst.nombre}</span>
        <span className="rendimientos-ola__card-rate">{inst.tasaRef}</span>
      </div>
      <div className="rendimientos-ola__card-meta">
        {inst.plazoDias?.[1] >= 9999 ? 'Para plata sin fecha' : `Para fechas a ${inst.plazoDias?.[0]}–${inst.plazoDias?.[1]} días`}
      </div>
      <p className="rendimientos-ola__card-why">{inst.porQue}</p>
      <div className={`rendimientos-ola__badge rendimientos-ola__badge--${estado.tone}`}>{estado.label}</div>
    </div>
  );
}

export default function BusinessRendimientos() {
  const [escalera, setEscalera] = useState(null);
  const [pos, setPos] = useState(null);
  const [error, setError] = useState('');
  const [verPolitica, setVerPolitica] = useState(false);

  const cargarPosicion = useCallback(() => {
    rendimientosPosicion().then((p) => { if (p && !p.sinEmpresa) setPos(p); }).catch(() => {});
  }, []);

  useEffect(() => {
    escaleraGet()
      .then((e) => {
        if (e?.sinEmpresa) setError('Tu usuario aún no tiene una empresa vinculada.');
        else setEscalera(e);
      })
      .catch((err) => {
        if (err?.response?.status === 403) setError('FlowexPay Business está en piloto privado.');
        else setError('No pude cargar la vitrina. Intenta de nuevo.');
      });
    cargarPosicion();
  }, [cargarPosicion]);

  if (error) {
    return (
      <div className="rendimientos-ola">
        <div className="rendimientos-ola__alert">{error}</div>
      </div>
    );
  }
  if (!escalera) {
    return (
      <div className="rendimientos-ola">
        <div className="rendimientos-ola__loading">Cargando la vitrina…</div>
      </div>
    );
  }

  const vitrina = escalera.politica?.vitrina || {};
  const cop = vitrina.COP || [];
  const usd = vitrina.USD || [];
  const exc = escalera.politica?.excedenteEstructural || {};

  return (
    <div className="rendimientos-ola">
      <header className="rendimientos-ola__head">
        <p className="rendimientos-ola__eyebrow">Tesorería · Rendimientos</p>
        <h1 className="rendimientos-ola__title">Rendimientos</h1>
        <p className="rendimientos-ola__sub">
          La plata que espera su fecha no tiene que dormir. Esto es lo que está permitido para la caja
          de una empresa — <strong>a la vista y de bajo riesgo</strong>, nada más.
        </p>
        {/* La regla de oro, arriba y sin letra chica: aquí nadie invierte por nadie. */}
        <p className="rendimientos-ola__regla">
          <strong>FlowexPay no mueve tu plata.</strong> Esta es la vitrina: te
          mostramos dónde puede trabajar cada provisión mientras espera — pero cada activación la decides
          tú y la confirmas con tu código, igual que un pago.
        </p>
      </header>

      {escalera.plataQuietaBancosCop > 0 && (
        <div className="rendimientos-ola__quieta">
          <div className="rendimientos-ola__quieta-title">
            Tienes {fmtCop(escalera.plataQuietaBancosCop)} quietos en bancos, al 0%
          </div>
          <p className="rendimientos-ola__quieta-sub">
            Según tu último extracto. Esa plata también puede trabajar hasta el día que la necesites — tú decides.
          </p>
        </div>
      )}

      <section>
        <div className="rendimientos-ola__section-lbl">
          En pesos — para la nómina, la DIAN, el arriendo
        </div>
        <div className="rendimientos-ola__stack">
          {cop.map((inst) => <TarjetaVitrina key={inst.id} inst={inst} />)}
        </div>
      </section>

      <section>
        <div className="rendimientos-ola__section-lbl">
          En dólares — si exportas o importas
        </div>
        <p className="rendimientos-ola__section-hint">
          El anticipo que te pagaron, la plata del contenedor a 60 días: la obligación en dólares rinde en dólares.
        </p>
        <div className="rendimientos-ola__stack">
          {usd.map((inst) => (
            inst.id === 'kamino'
              ? <TarjetaRewards key={inst.id} inst={inst} pos={pos} onRefresh={cargarPosicion} />
              : <TarjetaVitrina key={inst.id} inst={inst} />
          ))}
        </div>
      </section>

      <div className="rendimientos-ola__panel">
        <button type="button" onClick={() => setVerPolitica(!verPolitica)} className="rendimientos-ola__accordion">
          <span>¿Por qué la caja no va a acciones?</span>
          <span className="rendimientos-ola__accordion-chev" aria-hidden="true">{verPolitica ? '▲' : '▼'}</span>
        </button>
        {verPolitica && (
          <div className="rendimientos-ola__politica">
            <p>
              La plata de la empresa es capital de trabajo: casi toda tiene dueño y fecha. El objetivo no es
              el máximo retorno, es que <strong>no falte un peso el día del pago</strong>.
              Por eso la regla está escrita en el sistema, no en un consejo.
            </p>
            <p>{escalera.politica?.reglaMoneda}</p>
            <div className="rendimientos-ola__prohibidos">
              {(escalera.politica?.prohibidos || []).map((p) => (
                <div key={p.id} className="rendimientos-ola__prohibido">
                  <span className="rendimientos-ola__prohibido-id">{p.id}:</span>{' '}
                  <span>{p.motivo}</span>
                </div>
              ))}
            </div>
            <p className="rendimientos-ola__excedente">
              La única excepción: plata quieta hace más de {exc.mesesSinUsoMinimos ?? 3} meses
              puede ir a un portafolio conservador, con tope del {exc.topePctDeCajaPromedio ?? 25}%
              de la caja promedio y doble aprobación — quien lo propone no lo aprueba, igual que la nómina.
            </p>
          </div>
        )}
      </div>

      {/* El puente de vuelta: aquí se ve DÓNDE espera; cuánto y para cuándo, en Provisiones. */}
      <Link to="/business/provisiones" className="rendimientos-ola__bridge">
        <div className="rendimientos-ola__bridge-title">¿Cuánto deberías guardar y para cuándo?</div>
        <p className="rendimientos-ola__bridge-sub">
          El semáforo de tus fechas de pago y el reparto de cada ingreso → <strong>Provisiones</strong>
        </p>
      </Link>
    </div>
  );
}
